import axios from 'axios';
import { API_ROOT } from '../conf';
let authToken = null;
let authTokenLifetime = null;
let authTokenReceivedAt = 0;
let authSessionGeneration = 0;
/** A renewal was discarded because the session was cleared while it was in flight. */
export class AuthSessionEndedError extends Error {
    constructor() {
        super('The session ended while its access token was being renewed');
        this.name = 'AuthSessionEndedError';
    }
}
let authScope = 'customer';
export function setAuthScope(scope) {
    authScope = scope;
}
export function getAuthScope() {
    return authScope;
}
export function getAuthScopeHeaders() {
    return {
        'X-PB-Auth-Scope': authScope,
    };
}
function declaredLifetime(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const lifetime = payload.exp - payload.iat;
        return typeof payload.exp === 'number' && typeof payload.iat === 'number' && lifetime > 0 ? lifetime : null;
    }
    catch {
        return null;
    }
}
export function setAuthToken(token) {
    authToken = token || null;
    authTokenLifetime = authToken ? declaredLifetime(authToken) : null;
    authTokenReceivedAt = Date.now();
    window.localStorage.removeItem('token');
}
export function getAuthToken() {
    return authToken;
}
export function clearAuthToken() {
    authSessionGeneration += 1;
    authToken = null;
    authTokenLifetime = null;
    window.localStorage.removeItem('token');
}
/**
 * Whether the current token is in its renewal window. Age is measured from when
 * this tab received the token, so browser/server clock skew does not matter.
 */
export function isAuthTokenDue() {
    if (!authToken || authTokenLifetime === null) {
        return false;
    }
    const windowSeconds = Math.min(60, authTokenLifetime / 4);
    return Date.now() - authTokenReceivedAt >= (authTokenLifetime - windowSeconds) * 1000;
}
export async function refreshAuthToken() {
    const generation = authSessionGeneration;
    const resp = await axios
        .post(`${API_ROOT}/rest-auth/token/refresh-cookie/`, {}, {
        headers: getHeader(),
        withCredentials: true,
    })
        .then(responseBody)
        .catch(error => {
        if (generation !== authSessionGeneration) {
            throw new AuthSessionEndedError();
        }
        return handleErrors(error);
    });
    if (generation !== authSessionGeneration) {
        throw new AuthSessionEndedError();
    }
    if (resp?.token) {
        setAuthToken(resp.token);
    }
    return resp;
}
const handleErrors = (error) => {
    //need good error handling
    // Need throw the error to handle error events on the page were is called
    console.error(error);
    // if (!error?.response) {
    //     message.error(error?.message ? error?.message : 'Error');
    // } else if (error?.response?.status === 500) {
    //     message.error(error?.message ? error?.message : 'Error');
    // }
    throw error;
};
const responseBody = (resp) => {
    // right now both successful/unsuccessful calls go to the caller
    // routing them to a single component would be ideal to show errors
    // to the user
    return resp.data;
};
// axios >= 1 serializes a FormData body to JSON when Content-Type is application/json,
// so FormData bodies must leave Content-Type unset for the browser to send multipart.
function getHeader(has_files = false, data) {
    const token = getAuthToken();
    const contentType = data instanceof FormData ? {} : { 'Content-Type': has_files ? 'multipart/form-data' : 'application/json' };
    return {
        ...contentType,
        ...getAuthScopeHeaders(),
        ...(token ? { Authorization: `Token ${token}` } : {}),
    };
}
const requests = {
    get: (url, options) => axios
        .get(`${API_ROOT}${url}`, {
        headers: getHeader(),
        ...options,
    })
        .then(responseBody)
        .catch(handleErrors),
    post: (url, data = {}, options, has_files) => axios
        .post(`${API_ROOT}${url}`, data, {
        headers: getHeader(has_files, data),
        ...options,
    })
        .then(responseBody)
        .catch(handleErrors),
    patch: (url, data, has_files) => axios
        .patch(`${API_ROOT}${url}`, data, {
        headers: getHeader(has_files, data),
    })
        .then(responseBody)
        .catch(handleErrors),
    put: (url, data, has_files) => axios
        .put(`${API_ROOT}${url}`, data, {
        headers: getHeader(has_files, data),
    })
        .then(responseBody)
        .catch(handleErrors),
    delete: (url, options) => axios
        .delete(`${API_ROOT}${url}`, {
        headers: getHeader(),
        ...options,
    })
        .then(responseBody)
        .catch(handleErrors),
};
const { get, post, patch, put, delete: remove } = requests;
export { requests, get, patch, post, put, remove };
